import "@/App.css";
import { BrowserRouter, Navigate, Route, Routes } from "react-router-dom";
import { Toaster } from "sonner";

import { AuthProvider, useAuth } from "@/context/AuthContext";
import AdminCourseEditor from "@/pages/AdminCourseEditor";
import AdminTrainerEditPage from "@/pages/AdminTrainerEditPage";
import AdminBatchEditPage from "@/pages/AdminBatchEditPage";
import AdminBatchesPage from "@/pages/AdminBatchesPage";
import AdminCoursesPage from "@/pages/AdminCoursesPage";
import AdminReportsPage from "@/pages/AdminReportsPage";
import AdminTrainersPage from "@/pages/AdminTrainersPage";
import AdminTrainingCentersPage from "@/pages/AdminTrainingCentersPage";
import CenterManagerDashboard from "@/pages/CenterManagerDashboard";
import CenterManagerWorkspace from "@/pages/CenterManagerWorkspace";
import AdminTrainingCenterEditPage from "@/pages/AdminTrainingCenterEditPage";
import CoursePlayer from "@/pages/CoursePlayer";
import ForgotPasswordPage from "@/pages/ForgotPasswordPage";
import LearnerDashboard from "@/pages/LearnerDashboard";
import LoginPage from "@/pages/LoginPage";
import ResetPasswordPage from "@/pages/ResetPasswordPage";
import TrainerDashboard from "@/pages/TrainerDashboard";
import { rolePaths } from "@/lib/roles";

function RoleGate({ role, children }) {
  const { user, isLoading } = useAuth();
  if (isLoading) return <div className="loading-page" data-testid="auth-loading-state">Loading Skillexpress</div>;
  if (!user) return <Navigate to="/login" replace />;
  if (user.role !== role) return <Navigate to={rolePaths[user.role]} replace />;
  return children;
}

function EntryRoute() {
  const { user, isLoading } = useAuth();
  if (isLoading) return <div className="loading-page" data-testid="entry-loading-state">Loading Skillexpress</div>;
  return <Navigate to={user ? rolePaths[user.role] : "/login"} replace />;
}

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Routes>
          <Route path="/" element={<EntryRoute />} />
          <Route path="/login" element={<LoginPage audience="learner" />} />
          <Route path="/login/trainer" element={<LoginPage audience="trainer" />} />
          <Route path="/login/admin" element={<LoginPage audience="admin" />} />
          <Route path="/forgot-password" element={<ForgotPasswordPage />} />
          <Route path="/reset-password" element={<ResetPasswordPage />} />
          <Route path="/learner/dashboard" element={<RoleGate role="learner"><LearnerDashboard /></RoleGate>} />
          <Route path="/center-manager/dashboard" element={<RoleGate role="center_manager"><CenterManagerWorkspace /></RoleGate>} />
          <Route path="/learner/course/:courseId" element={<RoleGate role="learner"><CoursePlayer /></RoleGate>} />
          <Route path="/trainer/dashboard" element={<RoleGate role="trainer"><TrainerDashboard /></RoleGate>} />
          <Route path="/admin/dashboard" element={<Navigate to="/admin/courses" replace />} />
          <Route path="/admin/courses" element={<RoleGate role="admin"><AdminCoursesPage /></RoleGate>} />
          <Route path="/admin/centers" element={<RoleGate role="admin"><AdminTrainingCentersPage /></RoleGate>} />
          <Route path="/admin/centers/:centerId/edit" element={<RoleGate role="admin"><AdminTrainingCenterEditPage /></RoleGate>} />
          <Route path="/admin/trainers" element={<RoleGate role="admin"><AdminTrainersPage /></RoleGate>} />
          <Route path="/admin/batches" element={<RoleGate role="admin"><AdminBatchesPage /></RoleGate>} />
          <Route path="/admin/reports" element={<RoleGate role="admin"><AdminReportsPage /></RoleGate>} />
          <Route path="/admin/course/:courseId/edit" element={<RoleGate role="admin"><AdminCourseEditor /></RoleGate>} />
          <Route path="/admin/trainers/:trainerId/edit" element={<RoleGate role="admin"><AdminTrainerEditPage /></RoleGate>} />
          <Route path="/admin/batches/:batchId/edit" element={<RoleGate role="admin"><AdminBatchEditPage /></RoleGate>} />
          <Route path="*" element={<EntryRoute />} />
        </Routes>
        <Toaster richColors position="top-right" />
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
